import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { solicitudDetalleService } from '../../services';

const tipoEmpresaOptions = [
  { value: 'AGRUPACION', label: 'Agrupación' },
  { value: 'ASOCIACION', label: 'Asociación' },
  { value: 'COOPERATIVA', label: 'Coperativa' },
  { value: 'SOCIEDAD_ANONIMA', label: 'Sociedad anónima' },
  { value: 'SOCIEDAD_SIN_FINES_LUCR', label: 'Sociedad sin fines de lucro' },
  { value: 'SRL', label: 'SRL' },
  { value: 'SAS', label: 'SAS' },
  { value: 'FIDEICOMISO', label: 'Fideicomiso' },
  { value: 'OTRA', label: 'Otra' }
];

const usoFirmaOptions = [
  { value: 'INDISTINTA', label: 'Indistinta' },
  { value: 'CONJUNTA', label: 'Conjunta' }
];

const SeccionDatosPrincipalesEmpresa = ({ solicitudId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    denominacion: '',
    tipoEmpresa: '',
    telefono: '',
    celular: '',
    correoElectronico: '',
    usoFirma: '',
    actividad: '',
    comoNosConocio: ''
  });

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudDetalleService.getDatosPrincipalesEmpresa(solicitudId);
      if (response) {
        setFormData({
          denominacion: response.denominacion || '',
          tipoEmpresa: response.tipoEmpresa || '',
          telefono: response.telefono || '',
          celular: response.celular || '',
          correoElectronico: response.correoElectronico || '',
          usoFirma: response.usoFirma || '',
          actividad: response.actividad || '',
          comoNosConocio: response.comoNosConocio || ''
        });
      }
    } catch (err) {
      setError('Error al cargar los datos principales');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleInputChange = (field, value) => {
    if (field === 'telefono' || field === 'celular') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [field]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const isEmailValid = (email) => {
    if (!email) return false;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ...formData
      };
      await solicitudDetalleService.saveDatosPrincipalesEmpresa(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Datos Principales
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Denominación"
                required
                value={formData.denominacion}
                onChange={(e) => handleInputChange('denominacion', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth required>
                <InputLabel>Tipo de Empresa</InputLabel>
                <Select
                  value={formData.tipoEmpresa}
                  onChange={(e) => handleInputChange('tipoEmpresa', e.target.value)}
                  label="Tipo de Empresa"
                >
                  {tipoEmpresaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth required>
                <InputLabel>Uso de Firma</InputLabel>
                <Select
                  value={formData.usoFirma}
                  onChange={(e) => handleInputChange('usoFirma', e.target.value)}
                  label="Uso de Firma"
                >
                  {usoFirmaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Celular"
                value={formData.celular}
                onChange={(e) => handleInputChange('celular', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                required
                type="email"
                value={formData.correoElectronico}
                onChange={(e) => handleInputChange('correoElectronico', e.target.value)}
                variant="outlined"
                error={formData.correoElectronico !== '' && !isEmailValid(formData.correoElectronico)}
                helperText={
                  formData.correoElectronico !== '' && !isEmailValid(formData.correoElectronico)
                    ? 'Ingrese un correo electrónico válido'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Actividad"
                required
                value={formData.actividad}
                onChange={(e) => handleInputChange('actividad', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="¿Cómo nos conoció?"
                value={formData.comoNosConocio}
                onChange={(e) => handleInputChange('comoNosConocio', e.target.value)}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDatosPrincipalesEmpresa;

